import { useTheme } from '@emotion/react';
import { Box, Button } from '@sds-system/components';
import type { Theme } from '@sds-system/types';
import { useGetInternalProps } from '@sds-system/utils';
import { useMediaQuery } from 'usehooks-ts';
import type { AlertModalProps } from '..';
import { AlertModal, StyledConfirmModalButtonsWrapper } from '..';
import type { ConfirmModalProps } from './ConfirmModal.types';

export const ConfirmModal = (props: ConfirmModalProps) => {
  const theme = useTheme() as Theme;
  const internalProps = useGetInternalProps('ConfirmModal');
  const isSmallDevice = useMediaQuery(`(max-width: ${theme.breakpoint.tablet}px)`);
  const {
    fillButtonText,
    outlineButtonText,
    onClickFillButton,
    onClickOutlineButton,
    styledComponents,
    footer,
    buttonDirection = 'row',
    isResponsive,
    ...alertModalProps
  } = props;

  const alertStyledComponents: AlertModalProps['styledComponents'] = {
    alertRoot: styledComponents?.confirmRoot,
    content: styledComponents?.content,
    title: styledComponents?.title,
    actionButton: styledComponents?.actionButton,
    modalRoot: styledComponents?.modalRoot,
  };

  const innerIsResponsive = (() => {
    // 직접 전달된 isResponsive가 있으면 최우선 적용
    if (typeof isResponsive !== 'undefined') {
      return isResponsive;
    }

    // 테마에서 설정된 값이 있으면 그 다음 우선순위
    if (typeof internalProps?.isResponsive !== 'undefined') {
      return internalProps.isResponsive;
    }

    // 아무 설정이 없으면 기본값 true
    return true;
  })();

  const buttonSize = innerIsResponsive && !isSmallDevice ? 'XL' : 'L';

  return (
    <AlertModal
      {...alertModalProps}
      buttonText=''
      footer={
        <>
          <StyledConfirmModalButtonsWrapper
            flexDirection={buttonDirection === 'row' ? 'row' : 'column'}
            mt={24}
            {...(styledComponents?.buttonsWrapper && styledComponents.buttonsWrapper)}
          >
            <Button
              onClick={onClickOutlineButton}
              size={buttonSize}
              styledComponents={{
                root: {
                  width: '100%',
                },
              }}
              variant={'outline.assistive'}
              {...(styledComponents?.outlineButton && styledComponents.outlineButton)}
            >
              {outlineButtonText}
            </Button>
            <Button
              onClick={onClickFillButton}
              size={buttonSize}
              styledComponents={{
                root: {
                  width: '100%',
                },
              }}
              variant={'solid.primary'}
              {...(styledComponents?.fillButton && styledComponents.fillButton)}
            >
              {fillButtonText}
            </Button>
          </StyledConfirmModalButtonsWrapper>
          {footer && <Box height={16} />}
          {footer}
        </>
      }
      isResponsive={innerIsResponsive}
      styledComponents={alertStyledComponents}
    ></AlertModal>
  );
};
